import React, { useEffect, useState } from 'react';
import { XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart, ReferenceLine } from 'recharts';
import { AlertCircle, TrendingUp, Activity, CheckCircle2, TrendingDown } from 'lucide-react';
import axios from 'axios';
import { LoadingAnimation } from './LoadingAnimation';
interface ForecastChartProps {
  prediction_date?: string;
  title?: string;
  chartId?: string;
  showRealData?: boolean;
  substation?: string;
  showMetrics?: boolean;
}
export function ForecastChart({
  prediction_date = '2025-11-04',
  title = 'CMG (USD/MWH)',
  chartId = 'main',
  showRealData = true,
  substation,
  showMetrics = true
}: ForecastChartProps) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dayChanges, setDayChanges] = useState([]);
  const [diffData, setDiffData] = useState({
    avgDiffWithCoordinator: 0,
    avgDiffWithReal: 0,
    avgOurPrediction: 0,
    avgCoordinatorPrediction: 0
  });
  // Enhanced color palette for better dark mode
  const COLORS = {
    nuestra: '#3b82f6',
    coordinador: '#8b5cf6',
    real: '#10b981'
  };
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        // Build request body
        const requestBody: {
          prediction_date: string;
          substation?: string;
        } = {
          prediction_date: prediction_date
        };
        // Add substation if provided
        if (substation) {
          requestBody.substation = substation;
        }
        const response = await axios.post('https://api.vectrei.com/predict/day', requestBody, {
          headers: {
            'Content-Type': 'application/json'
          }
        });
        const rawData = response.data.predictions.map(item => {
          const date = new Date(item.prediction_timestamp);
          return {
            time: date,
            hour: date.getHours(),
            minute: date.getMinutes(),
            day: date.getDate(),
            displayTime: date.toLocaleTimeString('es-CL', {
              hour: '2-digit',
              minute: '2-digit',
              hour12: false
            }),
            displayDate: date.toLocaleDateString('es-CL', {
              month: 'short',
              day: 'numeric'
            }),
            NUESTRA: item.prediction,
            COORDINADOR: item.prog_cmg,
            REAL: showRealData && item.on_line_cmg !== null ? item.on_line_cmg : null,
            isGated: item.is_gated
          };
        });
        rawData.sort((a, b) => a.time - b.time);
        // Interpolate coordinator data
        const transformedData = rawData.map((item, index) => {
          let coordPrediction = item['COORDINADOR'];
          if (coordPrediction === null || coordPrediction === undefined) {
            let prevHourlyValue = null;
            let nextHourlyValue = null;
            for (let i = index - 1; i >= 0; i--) {
              if (rawData[i].minute === 0 && rawData[i]['COORDINADOR'] !== null) {
                prevHourlyValue = rawData[i]['COORDINADOR'];
                break;
              }
            }
            for (let i = index + 1; i < rawData.length; i++) {
              if (rawData[i].minute === 0 && rawData[i]['COORDINADOR'] !== null) {
                nextHourlyValue = rawData[i]['COORDINADOR'];
                break;
              }
            }
            if (prevHourlyValue !== null && nextHourlyValue !== null) {
              const minutesSincePrevHour = item.minute;
              const interpolationFactor = minutesSincePrevHour / 60;
              coordPrediction = prevHourlyValue + (nextHourlyValue - prevHourlyValue) * interpolationFactor;
            } else if (prevHourlyValue !== null) {
              coordPrediction = prevHourlyValue;
            } else if (nextHourlyValue !== null) {
              coordPrediction = nextHourlyValue;
            }
          }
          return {
            ...item,
            COORDINADOR: coordPrediction,
            'Diff Nuestra vs Coordinador': coordPrediction !== null ? Math.abs(item['NUESTRA'] - coordPrediction) : null,
            'Diff Nuestra vs Real': showRealData && item['REAL'] !== null ? Math.abs(item['NUESTRA'] - item['REAL']) : null,
            'Diff Coordinador vs Real': showRealData && item['REAL'] !== null && coordPrediction !== null ? Math.abs(coordPrediction - item['REAL']) : null
          };
        });
        setData(transformedData);
        const changes = transformedData.filter(item => item.hour === 0 && item.minute === 0).map(item => ({
          time: item.time,
          displayDate: item.displayDate,
          index: transformedData.findIndex(d => d.time.getTime() === item.time.getTime())
        }));
        setDayChanges(changes);
        // Calculate metrics
        const validCoordDiffs = transformedData.filter(item => item['Diff Nuestra vs Coordinador'] !== null);
        const validRealDiffs = transformedData.filter(item => item['Diff Nuestra vs Real'] !== null && item['Diff Coordinador vs Real'] !== null);
        let avgDiffCoord = 0;
        let avgDiffReal = 0;
        let avgOur = 0;
        let avgCoord = 0;
        let avgOurError = 0;
        let avgCoordError = 0;
        if (validCoordDiffs.length > 0) {
          avgDiffCoord = validCoordDiffs.reduce((sum, item) => sum + item['Diff Nuestra vs Coordinador'], 0) / validCoordDiffs.length;
          avgOur = validCoordDiffs.reduce((sum, item) => sum + item['NUESTRA'], 0) / validCoordDiffs.length;
          avgCoord = validCoordDiffs.reduce((sum, item) => sum + item['COORDINADOR'], 0) / validCoordDiffs.length;
        }
        if (validRealDiffs.length > 0) {
          avgOurError = validRealDiffs.reduce((sum, item) => sum + item['Diff Nuestra vs Real'], 0) / validRealDiffs.length;
          avgCoordError = validRealDiffs.reduce((sum, item) => sum + item['Diff Coordinador vs Real'], 0) / validRealDiffs.length;
          avgDiffReal = validRealDiffs.reduce((sum, item) => sum + (item['Diff Coordinador vs Real'] - item['Diff Nuestra vs Real']), 0) / validRealDiffs.length;
        }
        setDiffData({
          avgDiffWithCoordinator: avgDiffCoord,
          avgDiffWithReal: avgDiffReal,
          avgOurPrediction: avgOur,
          avgCoordinatorPrediction: avgCoord,
          avgOurError: avgOurError,
          avgCoordError: avgCoordError
        });
        setError(null);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError(err.message || 'Error al cargar los datos');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [prediction_date, showRealData, substation]);
  useEffect(() => {
    window[`forecastDiffData_${chartId}`] = diffData;
    const event = new CustomEvent(`forecastDiffDataUpdated_${chartId}`, {
      detail: diffData
    });
    window.dispatchEvent(event);
    return () => {
      delete window[`forecastDiffData_${chartId}`];
    };
  }, [diffData, chartId]);
  const errorDifference = Math.abs(diffData.avgDiffWithReal);
  const isImprovement = diffData.avgDiffWithReal > 0;
  const percentageDiff = diffData.avgCoordinatorPrediction !== 0 ? errorDifference / diffData.avgCoordinatorPrediction * 100 : 0;
  // Enhanced tooltip with better dark mode
  const CustomTooltip = ({
    active,
    payload,
    label
  }) => {
    if (!active || !payload || !payload.length) return null;
    const item = data.find(d => d.displayTime === label);
    const dateTimeLabel = item ? `${item.displayDate} ${item.displayTime}` : label;
    return <div className="rounded-xl p-4 shadow-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 backdrop-blur-sm" data-id="element-102">
        <div className="text-xs font-medium mb-3 text-slate-500 dark:text-slate-400" data-id="element-103">
          {dateTimeLabel}
        </div>
        <div className="space-y-2.5" data-id="element-104">
          {payload.map((entry, index) => {
          if (entry.value === null || entry.value === undefined) return null;
          const Icon = entry.name === 'NUESTRA' ? TrendingUp : entry.name === 'COORDINADOR' ? Activity : CheckCircle2;
          const color = entry.name === 'NUESTRA' ? COLORS.nuestra : entry.name === 'COORDINADOR' ? COLORS.coordinador : COLORS.real;
          return <div key={`tooltip-${index}`} className="flex justify-between items-center gap-6" data-id="element-105">
                <div className="flex items-center gap-2" data-id="element-106">
                  <Icon size={14} style={{
                color
              }} data-id="element-107" />
                  <span className="text-sm font-medium text-slate-700 dark:text-slate-200" data-id="element-108">
                    {entry.name}
                  </span>
                </div>
                <span className="font-semibold text-slate-900 dark:text-slate-50" data-id="element-109">
                  ${entry.value.toFixed(2)}
                </span>
              </div>;
        })}
        </div>

      </div>;
  };
  // Custom cursor
  const CustomCursor = props => {
    const {
      points,
      height
    } = props;
    if (!points || points.length === 0) return null;
    const x = points[0].x;
    return <line x1={x} y1={0} x2={x} y2={height} stroke="#94a3b8" strokeWidth={1} strokeDasharray="4 4" opacity={0.5} data-id="element-110" />;
  };
  if (loading) {
    return <div className="w-full p-8" data-id="element-111">
        {showMetrics && <div className="mb-6 flex justify-end" data-id="element-112">
            <div className="rounded-2xl p-6 min-w-[280px] shadow-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900" data-id="element-113">
              <div className="text-xs font-medium mb-4 text-slate-500 dark:text-slate-400 uppercase tracking-wide" data-id="element-114">
                Diferencia Promedio
              </div>
              <div className="space-y-3" data-id="element-115">
                <div className="flex items-baseline gap-3" data-id="element-116">
                  <div className="h-10 w-28 bg-gradient-to-r from-slate-200 to-slate-300 dark:from-slate-700 dark:to-slate-600 rounded-lg animate-pulse" data-id="element-117" />
                  <div className="h-6 w-20 bg-gradient-to-r from-slate-200 to-slate-300 dark:from-slate-700 dark:to-slate-600 rounded animate-pulse" data-id="element-118" />
                </div>
                <div className="h-8 w-48 bg-gradient-to-r from-slate-200 to-slate-300 dark:from-slate-700 dark:to-slate-600 rounded-full animate-pulse" data-id="element-119" />
                <div className="h-3 w-40 bg-gradient-to-r from-slate-200 to-slate-300 dark:from-slate-700 dark:to-slate-600 rounded animate-pulse" data-id="element-120" />
              </div>
            </div>
          </div>}
        <div className="flex flex-col items-center justify-center h-[500px]" data-id="element-121">
          <LoadingAnimation data-id="element-122" />
        </div>
      </div>;
  }
  if (error) {
    return <div className="w-full p-12 flex flex-col items-center justify-center h-[500px]" data-id="element-123">
        <div className="w-16 h-16 rounded-2xl bg-red-50 dark:bg-red-900/20 flex items-center justify-center mb-4" data-id="element-124">
          <AlertCircle size={32} className="text-red-600 dark:text-red-400" data-id="element-125" />
        </div>
        <h3 className="text-xl font-semibold mb-2 text-slate-900 dark:text-slate-50" data-id="element-126">
          Error al cargar datos
        </h3>
        <p className="text-sm text-slate-600 dark:text-slate-400 text-center max-w-md" data-id="element-127">
          {error}
        </p>
      </div>;
  }
  return <div className="w-full p-8" data-id="element-128">
      {/* Metrics Card */}
      {showMetrics && <div className="mb-6 flex justify-end" data-id="element-129">
          <div className="glass-card rounded-2xl p-6 min-w-[280px] shadow-lg border border-gray-200/50 dark:border-gray-800/50 hover:shadow-xl transition-shadow duration-300" data-id="element-130">
            <div className="text-xs font-medium mb-4 text-gray-500 dark:text-gray-400 uppercase tracking-wide" data-id="element-131">
              Diferencia Promedio
            </div>

            <div className="flex items-baseline gap-3 mb-4" data-id="element-132">
              <div className={`flex items-center gap-2 ${isImprovement ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`} data-id="element-133">
                {isImprovement ? <TrendingUp size={24} data-id="element-134" /> : <TrendingDown size={24} data-id="element-135" />}
                <span className="text-4xl font-bold" data-id="element-136">
                  {errorDifference.toFixed(2)}
                </span>
              </div>
              <span className="text-sm font-medium text-gray-600 dark:text-gray-400" data-id="element-137">
                USD/MWh
              </span>
            </div>

            <div className="space-y-2" data-id="element-138">
              <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium ${isImprovement ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300' : 'bg-rose-100 dark:bg-rose-900/30 text-rose-700 dark:text-rose-300'}`} data-id="element-139">
                {isImprovement && errorDifference !== 0 ? `${percentageDiff.toFixed(1)}% mejor que coordinador` : errorDifference === 0 ? 'Igual al coordinador' : `${percentageDiff.toFixed(1)}% peor que coordinador`}
              </div>

              <div className="text-xs text-gray-500 dark:text-gray-400" data-id="element-140">
                Basado en datos históricos
              </div>
            </div>
          </div>
        </div>}

      {/* Enhanced Legend with better dark mode */}
      <div className="mb-8 flex flex-wrap gap-6" data-id="element-141">
        <div className="flex items-center gap-2.5" data-id="element-142">
          <div className="w-3 h-3 rounded-full shadow-sm" style={{
          backgroundColor: COLORS.nuestra
        }} data-id="element-143" />
          <span className="text-sm font-medium text-slate-700 dark:text-slate-200" data-id="element-144">
            Nuestra
          </span>
        </div>
        <div className="flex items-center gap-2.5" data-id="element-145">
          <div className="w-3 h-3 rounded-full shadow-sm" style={{
          backgroundColor: COLORS.coordinador
        }} data-id="element-146" />
          <span className="text-sm font-medium text-slate-700 dark:text-slate-200" data-id="element-147">
            Coordinador (Programado)
          </span>
        </div>
        {showRealData && <div className="flex items-center gap-2.5" data-id="element-148">
            <div className="w-3 h-3 rounded-full shadow-sm" style={{
          backgroundColor: COLORS.real
        }} data-id="element-149" />
            <span className="text-sm font-medium text-slate-700 dark:text-slate-200" data-id="element-150">
              Real (On-line)
            </span>
          </div>}
      </div>

      {/* Chart */}
      <div className="h-[450px]" data-id="element-151">
        <ResponsiveContainer width="100%" height="100%" data-id="element-152">
          <AreaChart data={data} margin={{
          top: 10,
          right: 20,
          left: 10,
          bottom: 50
        }} data-id="element-153">
            <defs data-id="element-154">
              <linearGradient id={`colorNuestra-${chartId}`} x1="0" y1="0" x2="0" y2="1" data-id="element-155">
                <stop offset="0%" stopColor={COLORS.nuestra} stopOpacity={0.3} data-id="element-156" />
                <stop offset="100%" stopColor={COLORS.nuestra} stopOpacity={0} data-id="element-157" />
              </linearGradient>
              <linearGradient id={`colorCoordinador-${chartId}`} x1="0" y1="0" x2="0" y2="1" data-id="element-158">
                <stop offset="0%" stopColor={COLORS.coordinador} stopOpacity={0.3} data-id="element-159" />
                <stop offset="100%" stopColor={COLORS.coordinador} stopOpacity={0} data-id="element-160" />
              </linearGradient>
              <linearGradient id={`colorReal-${chartId}`} x1="0" y1="0" x2="0" y2="1" data-id="element-161">
                <stop offset="0%" stopColor={COLORS.real} stopOpacity={0.3} data-id="element-162" />
                <stop offset="100%" stopColor={COLORS.real} stopOpacity={0} data-id="element-163" />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" className="dark:stroke-gray-700" opacity={0.5} vertical={false} data-id="element-164" />
            <XAxis dataKey="displayTime" stroke="#9ca3af" className="dark:stroke-gray-400" strokeWidth={1} tick={{
            fontSize: 11,
            fill: '#6b7280',
            className: 'dark:fill-gray-300'
          }} tickFormatter={(time, index) => {
            const item = data[index];
            if (!item) return time;
            return item.hour % 3 === 0 && item.minute === 0 ? `${item.hour}:00` : '';
          }} label={{
            value: 'Hora',
            position: 'insideBottom',
            offset: -35,
            style: {
              fontSize: 11,
              fill: '#6b7280',
              fontWeight: 500
            },
            className: 'dark:fill-gray-300'
          }} data-id="element-165" />
            <YAxis stroke="#9ca3af" className="dark:stroke-gray-400" strokeWidth={1} tick={{
            fontSize: 11,
            fill: '#6b7280',
            className: 'dark:fill-gray-300'
          }} tickFormatter={value => `$${value}`} label={{
            value: title,
            angle: -90,
            position: 'insideLeft',
            style: {
              fontSize: 11,
              fill: '#6b7280',
              fontWeight: 500
            },
            className: 'dark:fill-gray-300'
          }} width={60} data-id="element-166" />
            <Tooltip content={<CustomTooltip data-id="element-168" />} cursor={<CustomCursor data-id="element-169" />} data-id="element-167" />
            {dayChanges.map((day, index) => <ReferenceLine key={index} x={day.index} stroke="#cbd5e1" strokeWidth={1} strokeDasharray="3 3" label={{
            value: day.displayDate,
            position: 'top',
            style: {
              fontSize: 10,
              fill: '#94a3b8',
              fontWeight: 500
            }
          }} data-id="element-170" />)}
            <Area type="monotone" dataKey="NUESTRA" stroke={COLORS.nuestra} strokeWidth={2.5} fill={`url(#colorNuestra-${chartId})`} dot={false} data-id="element-171" />
            <Area type="monotone" dataKey="COORDINADOR" stroke={COLORS.coordinador} strokeWidth={2.5} fill={`url(#colorCoordinador-${chartId})`} dot={false} data-id="element-172" />
            {showRealData && <Area type="monotone" dataKey="REAL" stroke={COLORS.real} strokeWidth={2.5} fill={`url(#colorReal-${chartId})`} dot={false} data-id="element-173" />}
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>;
}